import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Paper from "@mui/material/Paper";

import MauvelousPrimaryButton from "../../common/button/mauvelousPrimaryButton";
import CancelButton from "../../common/button/cancelButton";
import refundActions from "../../redux/refund/actions";

/**
 * Confirms the "Mark as Refunded" action for the rows the user ticked.
 *
 * The Refund Payment ID is the gateway's own id for the refund, taken from the merchant portal.
 * It is optional -- the transaction can be marked refunded without it -- but it is the only way
 * the refund log can later be tied back to the gateway, so it is worth asking for here rather
 * than only in the CSV.
 */
export default function MarkRefundedDialog({ open, rows, filters, onClose }) {
    const dispatch = useDispatch();
    const { markRefundErrors } = useSelector((state) => state.refundReducer);
    const { buttonLoader } = useSelector((state) => state.commonReducer);

    const [refundPaymentIds, setRefundPaymentIds] = useState({});

    // Each opening is a fresh confirmation: last time's typed ids and last time's rejections say
    // nothing about the rows selected now.
    useEffect(() => {
        if (open) {
            setRefundPaymentIds({});
            dispatch({ type: refundActions.SET_MARK_REFUND_ERRORS, payload: [] });
        }
    }, [open, dispatch]);

    const handleConfirm = () => {
        dispatch({
            type: refundActions.MARK_REFUNDED,
            payload: {
                refunds: rows.map((row) => ({
                    receiptno: row.receiptno,
                    refundpaymentid: refundPaymentIds[row.transactionsid]?.trim() || undefined
                })),
                filters,
                onSuccess: onClose
            }
        });
    };

    // The API refuses the whole selection if any row fails, so an error list means nothing was
    // written -- saying so plainly stops the user assuming the rest went through.
    const errorsByReceipt = new Map((markRefundErrors || []).map((e) => [e.receiptno, e.error]));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontSize: "18px", fontWeight: 600 }}>
                Mark as Refunded
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {rows.length} candidate(s) will be marked as refunded. Confirm only after the
                    money has actually been refunded on the payment gateway.
                </Typography>

                {markRefundErrors?.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Nothing was recorded. {markRefundErrors.length} row(s) were rejected — fix
                        them or clear them from the selection, then try again.
                    </Alert>
                )}

                <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #e0e0e0" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Candidate Name</TableCell>
                                <TableCell>Receipt No</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Refund Payment ID (optional)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const error = errorsByReceipt.get(row.receiptno);
                                return (
                                    <TableRow key={row.transactionsid}>
                                        <TableCell>{row.candidatename}</TableCell>
                                        <TableCell>{row.receiptno}</TableCell>
                                        <TableCell>{row.totalamount}</TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                placeholder="From the gateway"
                                                value={refundPaymentIds[row.transactionsid] || ""}
                                                onChange={(e) =>
                                                    setRefundPaymentIds((prev) => ({
                                                        ...prev,
                                                        [row.transactionsid]: e.target.value
                                                    }))
                                                }
                                                error={!!error}
                                                helperText={error || ""}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <CancelButton title="Cancel" handleBack={onClose} />
                    <MauvelousPrimaryButton
                        title={buttonLoader ? "loader" : "Confirm Refunded"}
                        handleClick={handleConfirm}
                        disabled={rows.length === 0 || buttonLoader}
                    />
                </Box>
            </DialogActions>
        </Dialog>
    );
}
